import { Cartesian2 } from "cesium";
import { AssetType } from "../types";
import { EarthBrainUtils } from "../utilities/earthbrainutility";
import { CrossSectionUtils } from "../utilities/cross-section-utils";
export class CrossSectionMeasurement {
    _outlines = [];
    _start;
    _end;
    pointCloudId;
    _site;
    _viewer;
    constructor(start, end, site, viewer) {
        this._start = start;
        this._end = end;
        this._site = site;
        this._viewer = viewer;
    }
    async update(start, end) {
        this._start = start;
        this._end = end;
        const { refPoints, localStart, localEnd } = await this._prepareRefPoints();
        this._outlines.length = 0;
        const results = await Promise.all([
            this._tracePointClouds(refPoints),
            this._traceDesignDatas(localStart, localEnd),
            this._traceRoads(refPoints),
            this._traceFlatGrounds(refPoints),
            this._traceReferenceTopologies(refPoints),
            this._traceSpreadingCompactions(localStart, localEnd),
        ]);
        for (const outlines of results) {
            this._outlines.push(...outlines);
        }
    }
    async updateByAssetType(assetType) {
        const { refPoints, localStart, localEnd } = await this._prepareRefPoints();
        // Remove existing outlines for this asset type
        this._outlines = this._outlines.filter((o) => o.assetType !== assetType);
        const traceFunction = this._getTraceFunctionForAssetType(assetType);
        if (!traceFunction)
            return;
        const newOutlines = await traceFunction(refPoints, localStart, localEnd);
        this._outlines.push(...newOutlines);
    }
    updateVisibility() { }
    getData() {
        return this._outlines;
    }
    dispose() {
        this._outlines = [];
    }
    zoomTo() { }
    async recalculate() {
        await this.update(this._start, this._end);
    }
    // --- Private: preparation ---
    async _prepareRefPoints() {
        const localPointsArray = await this._site.convertPointsToLocalSpace([
            this._start,
            this._end,
        ]);
        const refPoints = EarthBrainUtils.getLinearInterpolation(localPointsArray[0], localPointsArray[1], 0.5, true, true);
        return {
            refPoints,
            localStart: localPointsArray[0],
            localEnd: localPointsArray[1],
        };
    }
    _getTraceFunctionForAssetType(assetType) {
        switch (assetType) {
            case AssetType.Point_Cloud:
                return (refPoints) => this._tracePointClouds(refPoints);
            case AssetType.Design_Data:
                return (_r, localStart, localEnd) => this._traceDesignDatas(localStart, localEnd);
            case AssetType.Road_Data:
                return (refPoints) => this._traceRoads(refPoints);
            case AssetType.Flat_Ground_Data:
                return (refPoints) => this._traceFlatGrounds(refPoints);
            case AssetType.REFERENCE_TOPOLOGY:
                return (refPoints) => this._traceReferenceTopologies(refPoints);
            case AssetType.Spreading_Compaction_Data:
                return (_r, localStart, localEnd) => this._traceSpreadingCompactions(localStart, localEnd);
            default:
                return null;
        }
    }
    // --- Private: per-asset-type tracing ---
    async _tracePointClouds(refPoints) {
        const results = [];
        for (const pointCloud of this._site.getPointClouds()) {
            if (pointCloud.tileSet.show) {
                results.push(await this._createOutlineFromTraceable(pointCloud, pointCloud.heightMap, refPoints));
            }
        }
        return results;
    }
    async _traceDesignDatas(localStart, localEnd) {
        const results = [];
        for (const design of this._site.getDesignDatas()) {
            if (!design.tileSet.show)
                continue;
            const traceableMap = new Map([
                [AssetType.Design_Data, [design]],
            ]);
            const result = CrossSectionUtils.traceOutlineBetweenPoints(localStart, localEnd, 0.5, traceableMap);
            const designChunks = result.outline2D.get(AssetType.Design_Data)?.[0];
            results.push({
                assetType: AssetType.Design_Data,
                id: design.id,
                outline3D: [],
                outline2D: designChunks?.[0] ?? [],
            });
        }
        return results;
    }
    async _traceRoads(refPoints) {
        const results = [];
        for (const road of this._site.getRoads()) {
            if (road.isVisible()) {
                results.push(await this._createOutlineFromTraceable(road, road, refPoints));
            }
        }
        return results;
    }
    async _traceFlatGrounds(refPoints) {
        const results = [];
        for (const flatGround of this._site.getFlatGrounds()) {
            if (flatGround.isVisible()) {
                results.push(await this._createOutlineFromTraceable(flatGround, flatGround, refPoints));
            }
        }
        return results;
    }
    async _traceReferenceTopologies(refPoints) {
        const results = [];
        for (const referenceTopology of this._site.getReferenceTopologies()) {
            if (referenceTopology.getDrawing2DImage().isVisible()) {
                results.push(await this._createOutlineFromTraceable(referenceTopology, referenceTopology, refPoints));
            }
        }
        return results;
    }
    /**
     * Trace compaction surfaces only (spreading is excluded from cross-section).
     * emphasized determines 2D color: true → highlight, false → muted.
     */
    async _traceSpreadingCompactions(localStart, localEnd) {
        const results = [];
        for (const spreadingCompaction of this._site.getSpreadingCompactions()) {
            if (!spreadingCompaction.visible)
                continue;
            const compactionTraceables = spreadingCompaction
                .getTraceables()
                .filter((t) => t.type === "compaction");
            if (compactionTraceables.length === 0)
                continue;
            const traceableMap = new Map([
                [AssetType.Spreading_Compaction_Data, compactionTraceables],
            ]);
            const result = CrossSectionUtils.traceOutlineBetweenPoints(localStart, localEnd, 0.5, traceableMap);
            const outlinesBySurface = result.outline2D.get(AssetType.Spreading_Compaction_Data) ?? [];
            for (let i = 0; i < compactionTraceables.length; i++) {
                const chunks = outlinesBySurface[i];
                if (!chunks || chunks.length === 0)
                    continue;
                const outline = chunks[0];
                if (outline.length === 0)
                    continue;
                results.push({
                    assetType: AssetType.Spreading_Compaction_Data,
                    id: 0,
                    outline3D: [],
                    outline2D: outline,
                    emphasized: spreadingCompaction.isSurfaceVisibleByTypeOffset(compactionTraceables[i].type, compactionTraceables[i].offset),
                });
            }
        }
        return results;
    }
    // --- Private: helpers ---
    async _createOutlineFromTraceable(entity, traceable, refPoints) {
        const outlineLocal3D = traceable.tracePoints(refPoints);
        return {
            id: entity.id,
            assetType: entity.assetType,
            outline3D: await this._site.convertPointsToWGS84(outlineLocal3D.filter((p) => p !== undefined)),
            outline2D: this._create2DOutline(refPoints, outlineLocal3D),
        };
    }
    _create2DOutline(refPoints, pointsLocal) {
        const outline = [];
        for (let i = 0; i < pointsLocal.length; ++i) {
            if (pointsLocal[i]) {
                const dist = Cartesian2.distance(refPoints[0], pointsLocal[i]);
                outline.push([dist, pointsLocal[i].z]);
            }
        }
        return outline;
    }
}
//# sourceMappingURL=cross-section-measurement.js.map